"use client"

import * as React from "react"
import { useProjectMembers, useAddProjectMember, useUpdateProjectMember, useRemoveProjectMember, ProjectMemberDetail } from "@/hooks/use-projects"
import { useSearchUsers } from "@/hooks/use-users"
import { useRoles } from "@/hooks/use-roles"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { X, Search, UserPlus, Trash2, ChevronDown, Users } from "lucide-react"

interface ManageMembersDialogProps {
  projectId: string
  projectName: string
  isOpen: boolean
  onClose: () => void
}

export function ManageMembersDialog({ projectId, projectName, isOpen, onClose }: ManageMembersDialogProps) {
  const { toast } = useToast()
  const { data: members, isLoading: membersLoading } = useProjectMembers(projectId)
  const { data: rolesData, isLoading: rolesLoading } = useRoles()
  const roles = rolesData?.roles || []
  
  const addMember = useAddProjectMember()
  const updateMember = useUpdateProjectMember()
  const removeMember = useRemoveProjectMember()

  const [searchQuery, setSearchQuery] = React.useState("")
  const [showAddMember, setShowAddMember] = React.useState(false)
  const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null)
  const [selectedUserName, setSelectedUserName] = React.useState<string>("")
  const [selectedRoleId, setSelectedRoleId] = React.useState<string>("")
  const [showSearchDropdown, setShowSearchDropdown] = React.useState(false)

  const { data: searchResults, isLoading: searchLoading } = useSearchUsers(searchQuery)

  // Filter out users who are already members
  const availableUsers = React.useMemo(() => {
    if (!searchResults?.users || !members) return []
    const memberUserIds = new Set(members.map(m => m.user.id))
    return searchResults.users.filter(u => !memberUserIds.has(u.id))
  }, [searchResults, members])

  const handleAddMember = async () => {
    if (!selectedUserId || !selectedRoleId) {
      toast({
        title: "Error",
        description: "Please select a user and role",
        variant: "destructive",
      })
      return
    }

    try {
      await addMember.mutateAsync({
        projectId,
        userId: selectedUserId,
        roleId: selectedRoleId,
      })
      toast({
        title: "Member added",
        description: "The user has been added to the project.",
        variant: "success",
      })
      setShowAddMember(false)
      setSelectedUserId(null)
      setSelectedUserName("")
      setSelectedRoleId("")
      setSearchQuery("")
      setShowSearchDropdown(false)
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add member",
        variant: "destructive",
      })
    }
  }

  const handleUpdateRole = async (memberId: string, newRoleId: string) => {
    try {
      await updateMember.mutateAsync({
        projectId,
        memberId,
        roleId: newRoleId,
      })
      toast({
        title: "Role updated",
        description: "Member role has been updated.",
        variant: "success",
      })
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update role",
        variant: "destructive",
      })
    }
  }

  const handleRemoveMember = async (member: ProjectMemberDetail) => {
    if (!confirm(`Are you sure you want to remove ${member.user.name || member.user.email} from this project?`)) {
      return
    }

    try {
      await removeMember.mutateAsync({
        projectId,
        memberId: member.id,
      })
      toast({
        title: "Member removed",
        description: "The user has been removed from the project.",
        variant: "success",
      })
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to remove member",
        variant: "destructive",
      })
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div className="relative bg-white rounded-[14px] shadow-xl w-full max-w-[600px] max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-brand-primary" />
            <div>
              <h2 className="text-lg font-medium text-gray-900">Manage Members</h2>
              <p className="text-sm text-gray-500">{projectName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-[10px] hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Add Member Section */}
          {showAddMember ? (
            <div className="mb-6 p-4 bg-gray-50 rounded-[10px]">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Add New Member</h3>
              
              {/* User Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                
                {/* Show selected user or search input */}
                {selectedUserId && selectedUserName ? (
                  <div className="flex items-center gap-2 h-10 pl-10 pr-3 border border-brand-primary bg-brand-primary/5 rounded-[10px]">
                    <div className="w-6 h-6 rounded-full bg-brand-primary/20 flex items-center justify-center text-brand-primary text-xs font-medium">
                      {selectedUserName[0].toUpperCase()}
                    </div>
                    <span className="flex-1 text-gray-900 text-sm">{selectedUserName}</span>
                    <button
                      onClick={() => {
                        setSelectedUserId(null)
                        setSelectedUserName("")
                        setSearchQuery("")
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <Input
                    type="text"
                    placeholder="Search users by name or email..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value)
                      setShowSearchDropdown(true)
                    }}
                    onFocus={() => setShowSearchDropdown(true)}
                    className="pl-10"
                  />
                )}
                
                {/* Search Results Dropdown */}
                {showSearchDropdown && searchQuery.length >= 2 && !selectedUserId && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-[10px] shadow-lg max-h-48 overflow-auto">
                    {searchLoading ? (
                      <div className="p-3 text-center text-gray-500">Searching...</div>
                    ) : availableUsers.length === 0 ? (
                      <div className="p-3 text-center text-gray-500">No users found</div>
                    ) : (
                      availableUsers.map((user) => (
                        <button
                          key={user.id}
                          onClick={() => {
                            setSelectedUserId(user.id)
                            setSelectedUserName(user.name || user.email)
                            setSearchQuery("")
                            setShowSearchDropdown(false)
                          }}
                          className="w-full p-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors"
                        >
                          <div className="w-8 h-8 rounded-full bg-brand-primary/10 flex items-center justify-center text-brand-primary text-sm font-medium">
                            {(user.name || user.email)[0].toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{user.name || "Unnamed"}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Role Selection */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <div className="relative">
                  <select
                    value={selectedRoleId}
                    onChange={(e) => setSelectedRoleId(e.target.value)}
                    disabled={rolesLoading}
                    className="w-full h-11 px-4 pr-10 border border-gray-300 rounded-[10px] text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary appearance-none cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">{rolesLoading ? "Loading roles..." : "Select a role"}</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name.charAt(0).toUpperCase() + role.name.slice(1)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
                {!rolesLoading && roles.length === 0 && (
                  <p className="mt-1 text-xs text-amber-600">No roles available. Contact admin.</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddMember(false)
                    setSearchQuery("")
                    setSelectedUserId(null)
                    setSelectedUserName("")
                    setSelectedRoleId("")
                    setShowSearchDropdown(false)
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddMember}
                  disabled={!selectedUserId || !selectedRoleId || addMember.isPending}
                >
                  {addMember.isPending ? "Adding..." : "Add Member"}
                </Button>
              </div>
            </div>
          ) : null}

          {/* Add Member Button - always visible when not in add mode */}
          {!showAddMember && (
            <Button
              variant="outline"
              onClick={() => setShowAddMember(true)}
              className="mb-6 w-full justify-center gap-2"
            >
              <UserPlus className="w-4 h-4" />
              Add Member
            </Button>
          )}

          {/* Members List */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              Current Members ({members?.length || 0})
            </h3>
            
            {membersLoading ? (
              <div className="text-center py-8 text-gray-500">Loading members...</div>
            ) : members?.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No members yet</div>
            ) : (
              <div className="space-y-2">
                {members?.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-[10px]"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center text-brand-primary font-medium">
                        {(member.user.name || member.user.email)[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {member.user.name || "Unnamed"}
                        </div>
                        <div className="text-sm text-gray-500">{member.user.email}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Role Selector */}
                      <div className="relative">
                        <select
                          value={member.role.id}
                          onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                          disabled={updateMember.isPending || roles.length === 0}
                          className="h-10 pl-3 pr-9 border border-gray-200 rounded-[8px] text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary appearance-none cursor-pointer min-w-[120px]"
                        >
                          {roles.length === 0 ? (
                            <option value={member.role.id}>{member.role.name}</option>
                          ) : (
                            roles.map((role) => (
                              <option key={role.id} value={role.id}>
                                {role.name.charAt(0).toUpperCase() + role.name.slice(1)}
                              </option>
                            ))
                          )}
                        </select>
                        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => handleRemoveMember(member)}
                        disabled={removeMember.isPending}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-[8px] transition-colors"
                        title="Remove member"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>
  )
}

